import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEligibilityData } from "../useEligibilityData";
import { usePreventBack } from "../usePreventBack";
import Navbar from "../../../components/molecules/Navbar";
import Footer from "../../../components/molecules/Footer";
import Loader from "../../../components/atoms/Loader";
import BankIcon from "../../../images/static_assests/bank.svg";
import PdfIcon from "../../../images/static_assests/pdf.svg";
import styles from "../index.module.css";

// Institutes for which the bank-statement step is skipped entirely.
const BANK_STATEMENT_SKIP_INSTITUTE_IDS: string[] = ["1b6a250b-4a55-49d5-9cfd-eeedad264a8e"];

function EligibilityBankStatementOptions() {
  usePreventBack();
  const navigate = useNavigate();
  const userData = useEligibilityData();

  const [selected, setSelected] = useState<"pdf" | "aa" | "">("");
  const [file, setFile] = useState<File | null>(null);
  const [password, setPassword] = useState("");
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [checkingInstitute, setCheckingInstitute] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!userData?.userId || !userData?.applicationId) {
      setCheckingInstitute(false);
      return;
    }
    const token = sessionStorage.getItem("eligibility_auth_token");
    let cancelled = false;

    fetch(
      `${process.env.REACT_APP_DASHBOARD_URL}/summary/user?id=${userData.userId}&applicationId=${userData.applicationId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    )
      .then((res) => res.json())
      .then((res) => {
        if (cancelled) return;
        const rawInstId = res?.data?.instituteId;
        const instId = Array.isArray(rawInstId) ? rawInstId[0]?.id : rawInstId;
        if (instId && BANK_STATEMENT_SKIP_INSTITUTE_IDS.includes(instId)) {
          navigate("/eligibility/digilocker", { replace: true, state: { data: userData } });
          return;
        }
        setCheckingInstitute(false);
      })
      .catch(() => {
        if (!cancelled) setCheckingInstitute(false);
      });

    return () => { cancelled = true; };
  }, [userData?.userId, userData?.applicationId]);

  if (checkingInstitute) {
    return (
      <div className={styles.body}>
        <div className={styles.backdrop}>
          <Navbar />
          <div className={styles.main}>
            <div className={styles.cardWrapper}>
              <div className={styles.container} style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "260px" }}>
                <Loader />
              </div>
              <Footer light />
            </div>
          </div>
        </div>
      </div>
    );
  }

  const getOnemoneyLink = async () => {
    try {
      const token = sessionStorage.getItem("eligibility_auth_token");
      const headers: Record<string, string> = { "Content-Type": "application/json" };
      if (token) headers["Authorization"] = `Bearer ${token}`;

      const response = await fetch(
        `${process.env.REACT_APP_DASHBOARD_URL}/onemoney/generate`,
        {
          method: "POST",
          headers,
          body: JSON.stringify({
            userId: userData.userId,
            applicationId: userData.applicationId,
            mobile: userData.mobile,
            redirectUrl: `${window.location.origin}/eligibility/waiting`,
          }),
        }
      );
      const result = await response.json();
      const onemoneyUrl = result?.data?.url;
      if (!onemoneyUrl) {
        setError("Failed to get OneMoney link. Please try again.");
        return;
      }

      sessionStorage.setItem("bank_statement_method", "aa");
      sessionStorage.setItem("eligibility_next_route", "/eligibility/digilocker");
      // store identifiers so the waiting page can poll status
      sessionStorage.setItem("onemoney_meta", JSON.stringify({
        userId: userData.userId,
        applicationId: userData.applicationId,
        mobile: userData.mobile,
      }));

      window.location.href = onemoneyUrl;
    } catch {
      setError("Failed to get OneMoney link. Please try again.");
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (selected === "aa") {
      setUploading(true);
      await getOnemoneyLink();
      setUploading(false);
      return;
    }
    if (selected === "pdf" && file) {
      setUploading(true);
      try {
        const token = sessionStorage.getItem("eligibility_auth_token");
        const formData = new FormData();
        formData.append("files", file);
        formData.append("userId", userData.userId);
        formData.append("applicationId", userData.applicationId);
        formData.append("type", "pdf");
        formData.append("password", password);

        const res = await fetch(
          `${process.env.REACT_APP_DASHBOARD_URL}/bank-statement-analysis/finbox/upload/multiple`,
          {
            method: "POST",
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: formData,
          }
        );
        const result = await res.json();
        if (!res.ok || result.error) {
          setError(result?.message || "Oops! There is an issue at our end. Please contact us.");
        } else if (result.data && typeof result.data === "object" && result.data.statement_id) {
          sessionStorage.setItem("bank_statement_method", "pdf");
          navigate("/eligibility/waiting", { replace: true, state: { data: userData, nextRoute: "/eligibility/digilocker" } });
        } else {
          setError("Statement could not be processed. Please try the Account Aggregator option or upload a different statement.");
        }
      } catch (e: any) {
        setError(e?.message || "Oops! There is an issue at our end. Please contact us.");
      } finally {
        setUploading(false);
      }
    }
  };

  const handleSelectOption = (id: "pdf" | "aa") => {
    setSelected(id);
    if (id === "aa") setFile(null);
    setError("");
  };

  const optionCard = (
    id: "pdf" | "aa",
    icon: string,
    title: string,
    subtitle: string,
    badge?: string
  ) => (
    <div
      onClick={() => handleSelectOption(id)}
      style={{
        borderRadius: 10,
        border: selected === id ? "1.5px solid #d32028" : "1px solid var(--primary-border)",
        background: selected === id ? "rgba(211, 32, 40, 0.05)" : "var(--white)",
        cursor: "pointer",
        transition: "all 0.15s ease",
        marginBottom: "0.75rem",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "1rem", padding: "1rem" }}>
        <div style={{
          width: "2.8rem", height: "2.8rem", borderRadius: "50%",
          background: selected === id ? "rgba(211,32,40,0.1)" : "#f5f5f5",
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0, transition: "background 0.15s ease",
        }}>
          <img src={icon} alt={title} style={{ width: "1.5rem", height: "1.5rem" }} />
        </div>
        <div>
          <div style={{ fontWeight: 600, fontSize: "0.75rem", color: "var(--label-black)", marginBottom: "0.15rem",whiteSpace:'nowrap' }}>
            {title}
          </div>
          <div style={{ fontSize: "0.58rem", color: "#888" }}>{subtitle}</div>
        </div>
        {selected === id && (
          <div style={{
            marginLeft: "auto", width: "1.1rem", height: "1.1rem", borderRadius: "50%",
            background: "#d32028", display: "flex", alignItems: "center",
            justifyContent: "center", flexShrink: 0,
          }}>
            <svg width="7" height="6" viewBox="0 0 7 6" fill="none">
              <path d="M1 3L2.8 5L6 1" stroke="white" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        )}
      </div>
      {badge && (
        <div style={{
          background: "#2e7d32",
          color: "#fff",
          fontSize: "0.72rem",
          fontWeight: 600,
          textAlign: "center",
          padding: "0.3rem 0.5rem",
          letterSpacing: "0.3px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "0.35rem",
        }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
            <path d="M13 2L3 14H12L11 22L21 10H12L13 2Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="white" />
          </svg>
          {badge}
        </div>
      )}
    </div>
  );

  return (
    <div className={styles.body}>
      <div className={styles.backdrop}>
        <Navbar />
        <div className={styles.main}>
          <div className={styles.cardWrapper}>
            <div className={styles.container}>
              <div className={styles.cardHeader} >
                <img src={BankIcon} alt="" style={{height:'2.3rem',width:"2.3rem",padding:"0.5rem"}}/>
                <p className={styles.cardTitle}>Bank Statement</p>
              </div>
              <p style={{ fontSize: "0.83rem", color: "#888", margin: "0 0 1rem 0" }}>
               Please select your salary bank account to complete the verification.
              </p>
              <div className={styles.content}>
                {optionCard("aa", BankIcon, "Fetch using Phone Number", "Powered by RBI Regulated Account Aggregator", "Fast & Recommended")}

                {optionCard("pdf", PdfIcon, "Upload PDF", "Upload your bank statement as a PDF file")}

                {selected === "pdf" && (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf"
                      style={{ display: "none" }}
                      onChange={(e) => {
                        const f = e.target.files?.[0] || null;
                        setFile(f);
                        setError("");
                      }}
                    />
                    {!file ? (
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        style={{
                          border: "1.5px dashed #d32028", borderRadius: 10,
                          padding: "1.2rem", display: "flex", flexDirection: "column",
                          alignItems: "center", justifyContent: "center",
                          gap: "0.4rem", cursor: "pointer",
                          background: "rgba(211,32,40,0.02)",
                        }}
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                          <path d="M12 16V8M12 8L9 11M12 8L15 11" stroke="#d32028" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M20 16.7C21.2 15.9 22 14.5 22 13C22 10.8 20.2 9 18 9C17.7 9 17.4 9 17.1 9.1C16.5 6.7 14.4 5 12 5C9.2 5 7 7.2 7 10C7 10.1 7 10.3 7 10.4C5.3 10.9 4 12.5 4 14.5C4 16.7 5.7 18.5 7.9 18.9" stroke="#d32028" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "#d32028" }}>Tap to upload PDF</span>
                        <span style={{ fontSize: "0.75rem", color: "#aaa" }}>PDF files only</span>
                      </div>
                    ) : (
                      <>
                        <div style={{
                          display: "flex", alignItems: "center", gap: "0.6rem",
                          padding: "0.75rem 1rem", borderRadius: 10,
                          border: "1.5px solid #d32028",
                          background: "rgba(211,32,40,0.04)",
                        }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#d32028" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M14 2V8H20" stroke="#d32028" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                          <span style={{ fontSize: "0.84rem", color: "#222", fontWeight: 500, flex: 1 }}>
                            {file.name.length > 28 ? file.name.slice(0, 25) + "..." : file.name}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setFile(null);
                              setPassword("");
                              if (fileInputRef.current) fileInputRef.current.value = "";
                            }}
                            style={{
                              flexShrink: 0, width: "1.5rem", height: "1.5rem",
                              borderRadius: "50%", background: "#eee", border: "none",
                              cursor: "pointer", display: "flex", alignItems: "center",
                              justifyContent: "center",marginTop:'-0.2rem'
                            }}
                          >
                            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                              <path d="M10 2L2 10M2 2L10 10" stroke="#666" strokeWidth="1.8" strokeLinecap="round" />
                            </svg>
                          </button>
                        </div>
                        <input
                          type="password"
                          placeholder="PDF password (if any)"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          style={{
                            marginTop: "0.5rem", width: "100%",
                            padding: "0.65rem 0.9rem", borderRadius: 8,
                            border: "1px solid var(--primary-border)",
                            fontSize: "0.84rem", outline: "none",
                            boxSizing: "border-box",
                            fontFamily: "Outfit, sans-serif",
                          }}
                        />
                      </>
                    )}
                  </div>
                )}
              </div>

              {error && (
                <p style={{ color: "#d32028", fontSize: "0.85rem", marginTop: "0.75rem", marginBottom: 0 }}>
                  {error}
                </p>
              )}
              {uploading && <Loader />}
            </div>

            <div style={{ padding: "0rem 1.25rem 1rem", display: "flex", flexDirection: "row", alignItems: "center",justifyContent:'center', gap: "0.6rem" }}>
              <button
                onClick={handleSubmit}
                disabled={!selected || (selected === "pdf" && !file) || uploading}
                style={{
                  width: "65%",
                  padding: "0.8rem 1.5rem",
                  background: (!selected || (selected === "pdf" && !file) || uploading) ? "#aaa" : "#d32028",
                  color: "#fff",
                  border: "none",
                  borderRadius: "60px",
                  fontSize: "0.9rem",
                  fontWeight: 700,
                  fontFamily: "Outfit, sans-serif",
                  cursor: (!selected || (selected === "pdf" && !file) || uploading) ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                }}
              >
                {uploading ? (selected === "aa" ? "Please wait..." : "Uploading...") : (
                  <>
                    Submit & Continue
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <path d="M5 12H19M13 6L19 12L13 18" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </>
                )}
              </button>
              {process.env.REACT_APP_CURRENT !== 'prod' && (
                <button
                  onClick={() => navigate("/eligibility/digilocker", { state: { data: userData } })}
                  style={{ background: "none", border: "none", color: "#bbb", fontSize: "0.78rem", cursor: "pointer", fontFamily: "Outfit, sans-serif", textDecoration: "underline" }}
                >
                  Skip
                </button>
              )}
            </div>
          <Footer light />
          </div>
        </div>
      </div>
    </div>
  );
}

export default EligibilityBankStatementOptions;
